import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';

import { AuthenticationService } from '../../services/authentication_service';

const passwordPolicyValidator = (): ValidatorFn => {
  return (control): ValidationErrors | null => {
    const value = String(control.value || '');
    if (!value) {
      return null;
    }

    const hasAllowedCapital = /[A-NP-Z]/.test(value);
    const hasAllowedSmall = /[a-np-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasLength = value.length >= 8;

    return hasLength && hasAllowedCapital && hasAllowedSmall && hasNumber
      ? null
      : { passwordPolicy: true };
  };
};

const passwordMatchValidator: ValidatorFn = (control) => {
  const newPassword = control.get('new_password')?.value;
  const confirmPassword = control.get('confirm_password')?.value;
  if (!newPassword || !confirmPassword) {
    return null;
  }
  return newPassword === confirmPassword ? null : { passwordMismatch: true };
};

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthenticationService);
  private readonly router = inject(Router);
  message = '';
  successMessage = '';

  readonly form = this.fb.nonNullable.group({
    current_password: ['', [Validators.required, Validators.minLength(8)]],
    new_password: ['', [Validators.required, Validators.minLength(8), passwordPolicyValidator()]],
    confirm_password: ['', [Validators.required]]
  }, { validators: [passwordMatchValidator] });

  submit(): void {
    this.message = '';
    this.successMessage = '';
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { current_password, new_password } = this.form.getRawValue();
    this.authService.changePassword({ current_password, new_password }).subscribe({
      next: () => {
        this.successMessage = 'Password changed successfully.';
        this.form.reset({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        void this.router.navigate(['/dashboard']);
      },
      error: () => {
        this.message = 'Unable to change password. Check your current password and the password rules.';
      }
    });
  }

  get newPasswordHint(): string {
    return 'Minimum 8 characters, one capital letter except O, one small letter except o, and one number.';
  }
}
